import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { buildCsv, downloadCsv } from "@/lib/csv";
import { savePdfA4Portrait_Results } from "@/lib/pdf";
import { formatDMY } from "@/lib/date";
import { usePrintStylesheet } from "@/pages/printStyles";

const PLACEMENT_LABEL = {
  FIRST: "1st", SECOND: "2nd", THIRD: "3rd",
  HIGHLY_COMMENDED: "Highly Commended", COMMENDED: "Commended", NONE: "",
};

export default function PrintResults() {
  const { competitionId } = useParams();
  const [data, setData] = useState(null);
  const [season, setSeason] = useState(null);

  usePrintStylesheet();

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/stats/competition/${competitionId}`);
      setData(data);
      if (data.competition?.season_id) {
        const seasons = (await api.get("/seasons")).data;
        setSeason(seasons.find((s) => s.id === data.competition.season_id));
      }
    })();
  }, [competitionId]);

  // Set the tab / browser print-header title so the OS printer chrome shows
  // the competition name instead of the app URL. Restored on unmount.
  useEffect(() => {
    if (!data?.competition?.competition_name) return;
    const prev = document.title;
    document.title = `${data.competition.competition_name} — Results`;
    return () => { document.title = prev; };
  }, [data]);

  if (!data) return <div style={{ padding: 40, color: "#555", background: "#fff" }}>Loading results…</div>;
  const { competition, entries, club_name: clubName } = data;

  const exportCsv = () => {
    const headers = ["Rank", "I.D.", "Photographer", "Image Title", "Score", "Place"];
    const csvRows = entries.map((e, i) => ({
      Rank: i + 1,
      "I.D.": e.photographer_code,
      Photographer: e.photographer_name,
      "Image Title": e.image_title,
      Score: e.score,
      Place: PLACEMENT_LABEL[e.placement] || "",
    }));
    const safe = (competition.competition_name || "results").replace(/[^a-z0-9-_]+/gi, "_");
    downloadCsv(`${safe}_results.csv`, buildCsv(headers, csvRows));
  };

  return (
    <div className="cc-print">
      <div className="cc-print-toolbar">
        <button
          onClick={() => savePdfA4Portrait_Results({ competition, season, entries, clubName })}
          className="cc-print-btn primary"
          data-testid="results-print-button"
        >
          Print (PDF)
        </button>
        <button onClick={exportCsv} className="cc-print-btn" data-testid="results-export-csv-button">Export CSV</button>
        <button onClick={() => window.close()} className="cc-print-btn">Close</button>
      </div>

      <header className="cc-print-header">
        {clubName ? <div className="club-name">{clubName}</div> : null}
        <div className="comp-title">{competition.competition_name}</div>
        <div className="meta-row">
          <span><strong>Date:</strong> {formatDMY(competition.competition_date)}</span>
          <span><strong>Type:</strong> {competition.competition_type}</span>
          <span><strong>Category:</strong> {competition.category}</span>
          {competition.round_number ? <span><strong>Round:</strong> {competition.round_number}</span> : null}
          {competition.judge_name ? <span><strong>Judge:</strong> {competition.judge_name}</span> : null}
          {season ? <span><strong>Season:</strong> {season.season_name}</span> : null}
        </div>
        <div className="page-sub">Results Report</div>
      </header>

      <div className="cc-print-summary">
        <div><strong>{entries.length}</strong> entries</div>
        <div>Highest score: <strong>{entries.length ? entries[0].score : "—"}</strong></div>
      </div>

      <table className="cc-print-table">
        <colgroup>
          <col style={{ width: "7%" }} />
          <col style={{ width: "11%" }} />
          <col style={{ width: "23%" }} />
          <col style={{ width: "37%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "12%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Rank</th><th>I.D.</th><th>Photographer</th><th>Image Title</th><th>Score</th><th>Place</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={e.id}>
              <td className="num">{i + 1}</td>
              <td className="mono">{e.photographer_code}</td>
              <td>{e.photographer_name}</td>
              <td>{e.image_title}</td>
              <td className="score">{e.score}</td>
              <td className={`pcell pcell-${e.placement}`}>{PLACEMENT_LABEL[e.placement] || ""}</td>
            </tr>
          ))}
          {entries.length === 0 && (
            <tr><td colSpan={6} style={{ textAlign: "center", padding: 30, color: "#888" }}>
              No entries scored yet.
            </td></tr>
          )}
        </tbody>
      </table>

      <footer className="cc-print-footer">
        Camera Club Score · printed {new Date().toLocaleString()}
      </footer>
    </div>
  );
}
